import axios from 'axios';
import { apiClient, getApiBaseUrl } from '../axiosInstance';
import { ENDPOINTS } from '../endpoints';
import { extractAwsFields } from '../../stores/useAppointmentsStore';
import type { JobService } from './interfaces/JobService';
import { adaptJob, type JobWire } from './adapters/job';
import { uploadSingleVideo } from './shared/videoUpload';

// Raw axios instance for S3 presigned POSTs. We deliberately bypass
// `apiClient` so its request interceptor does NOT (a) prefix the S3 URL
// with the Sera API baseURL or (b) attach the Sera bearer token to the
// S3 request — both would invalidate the presigned signature.
const s3Client = axios.create();

const ALLOWED_DOC_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
const DOC_MAX_BYTES = 20 * 1024 * 1024;

export const jobService: JobService = {
  async getJob(id) {
    // The /v2/jobs/:id wire shape includes nested fields (invoice, etc.) and
    // omits the legacy `settings` block that lived on the Vue 3 `Job` type.
    // See adapters/job.ts for the full mapping.
    const { data } = await apiClient.get<JobWire>(ENDPOINTS.v2.jobs.get(id));
    return adaptJob(data);
  },

  // eslint-disable-next-line max-lines-per-function -- pre-existing length; refactor out of scope for bug fix
  async uploadPhotos(files, jobId, onProgress?, onStart?, signalFor?) {
    // Wire contract notes (see .planning/debug/s3-post-never-fires.md):
    //
    //   • Request body:  `{ extensions: string[] }`. The Rails permit list at
    //     api/app/controllers/v2/jobs/photos_controller.rb#L74 accepts only
    //     `extensions`; sending `file_extensions` silently drops the list and
    //     the reservation array returns empty -- which made every per-file
    //     promise reject with "No reservation for index N" BEFORE the S3 POST,
    //     so the upload never reached the network. The Vue 2 legacy client
    //     (consumer-portal/src/store.ts) sends `{ extensions }`; match that.
    //
    //   • Response shape: each reservation is `{ server_url, success_url,
    //     parameters: { ... } }`. The field is `parameters`, NOT `aws_params`
    //     -- see api/app/views/v2/photos/_reserve_s3.json.jbuilder. Use the
    //     shared `extractAwsFields` helper so the upload stays tolerant of
    //     either name in case the API ever renames again, and so the photo
    //     and document upload paths read reservations the same way.
    const jobData = await jobService.getJob(jobId);
    const reserveHref = jobData.links.reserve_s3_photos.href;
    const extensions = files.map((f) => f.name.split('.').pop() ?? 'jpg');
    const { data: reservations } = await apiClient.post<
      Array<{
        server_url: string;
        parameters?: Record<string, string>;
        aws_params?: Record<string, string>;
        success_url: string;
      }>
    >(reserveHref, { extensions });

    const apiBaseUrl = getApiBaseUrl();
    const allowedHost = new URL(apiBaseUrl).hostname;

    const results = await Promise.allSettled(
      // eslint-disable-next-line complexity -- per-file upload body covers reserve→S3→progress→success-url validation→confirm + abort signal threading; splitting hurts readability more than it helps
      files.map(async (file, i) => {
        const reservation = reservations[i];
        if (!reservation) throw new Error(`No reservation for index ${i}`);
        const formData = new FormData();
        // S3 presigned POST policy requires every signature field to come
        // BEFORE the `file` part; FormData preserves append order, so append
        // params first then the file.
        const params = extractAwsFields(reservation);
        Object.entries(params).forEach(([k, v]) =>
          formData.append(k, String(v)),
        );
        formData.append('file', file);
        onStart?.(file);
        // Emit a 0% event right after onStart so subscribers can mark the
        // file "in flight with progress" before the first byte ships.
        // axios fires the first onUploadProgress slightly later than onStart.
        onProgress?.({ file, percent: 0 });
        let lastReportedPct = 0;
        const signal = signalFor?.(file);
        try {
          await s3Client.post(reservation.server_url, formData, {
            signal,
            onUploadProgress: (e) => {
              const total = e.total ?? file.size;
              if (!total) return;
              // Cap mid-upload at 99% so the in-flight Set is only drained on
              // the final 100% emit (matches the video upload pattern).
              const pct = Math.min(99, Math.round((e.loaded / total) * 100));
              if (pct !== lastReportedPct) {
                lastReportedPct = pct;
                onProgress?.({ file, percent: pct });
              }
            },
          });
        } catch (err) {
          const axErr = err as {
            response?: { status?: number; data?: unknown };
          };
          const status = axErr.response?.status ?? 'unknown';
          throw new Error(`S3 photo upload failed (${status})`);
        }
        const successUrl = new URL(reservation.success_url, apiBaseUrl);
        // Strict equality or proper subdomain only. `endsWith(allowedHost)`
        // accepts evildemo.api.sera.tech when allowedHost is
        // demo.api.sera.tech -- a suffix-bypass. Reject anything that
        // isn't either an exact match or a *.allowedHost subdomain.
        const incomingHost = successUrl.hostname;
        const isExactHost = incomingHost === allowedHost;
        const isSubdomain = incomingHost.endsWith('.' + allowedHost);
        if (!isExactHost && !isSubdomain) {
          throw new Error(`Untrusted success_url host: ${incomingHost}`);
        }
        await apiClient.post(
          reservation.success_url,
          { source: 'customer_portal' },
          { signal },
        );
        // Final completion event drains the in-flight Set at the store level.
        onProgress?.({ file, percent: 100 });
      }),
    );
    return {
      failedCount: results.filter((r) => r.status === 'rejected').length,
    };
  },

  // eslint-disable-next-line max-lines-per-function -- two-phase mime validation + reserve + S3 upload with progress lifts body slightly past 50; splitting would fragment the upload flow
  async uploadDocuments(files, jobId, onProgress?, onStart?, signalFor?) {
    const valid = files.filter((f) => {
      const okType =
        (ALLOWED_DOC_MIME as readonly string[]).includes(f.type) ||
        /\.(pdf|docx?)$/i.test(f.name);
      const okSize = f.size <= DOC_MAX_BYTES;
      return okType && okSize;
    });
    if (valid.length === 0) return { failedCount: files.length };

    const extensions = valid.map((f) => f.name.split('.').pop() ?? 'pdf');
    const { data: reservations } = await apiClient.post<
      Array<{
        server_url: string;
        parameters?: Record<string, string>;
        aws_params?: Record<string, string>;
      }>
    >(ENDPOINTS.v2.jobs.documentsReserve(jobId), { extensions });

    const results = await Promise.allSettled(
      valid.map(async (file, i) => {
        const reservation = reservations[i];
        const params = extractAwsFields(reservation);
        const formData = new FormData();
        Object.entries(params).forEach(([k, v]) =>
          formData.append(k, String(v)),
        );
        formData.append('file', file);
        onStart?.(file);
        onProgress?.({ file, percent: 0 });
        let lastReportedPct = 0;
        const signal = signalFor?.(file);
        try {
          await s3Client.post(reservation.server_url, formData, {
            signal,
            onUploadProgress: (e) => {
              const total = e.total ?? file.size;
              if (!total) return;
              const pct = Math.min(99, Math.round((e.loaded / total) * 100));
              if (pct !== lastReportedPct) {
                lastReportedPct = pct;
                onProgress?.({ file, percent: pct });
              }
            },
          });
        } catch (err) {
          const axErr = err as { response?: { status?: number } };
          const status = axErr.response?.status ?? 'unknown';
          throw new Error(`S3 document upload failed (${status})`);
        }
        const key = params['key'] ?? '';
        await apiClient.post(
          ENDPOINTS.v2.jobs.documentsComplete(jobId, encodeURIComponent(key)),
          { filename: file.name },
          { signal },
        );
        onProgress?.({ file, percent: 100 });
      }),
    );
    return {
      failedCount: results.filter((r) => r.status === 'rejected').length,
    };
  },

  async uploadVideos(files, jobId, onProgress?, onStart?, signalFor?) {
    const fileList = Array.isArray(files) ? files : [files];
    const results = await Promise.allSettled(
      fileList.map((file) =>
        uploadSingleVideo(file, jobId, onProgress, onStart, signalFor?.(file)),
      ),
    );
    return {
      failedCount: results.filter((r) => r.status === 'rejected').length,
    };
  },
};
